// ============================================================================
// adminTemakView.js — "Témák kezelése" az admin panelben (2026-10-19, v111): egy szoba témáinak átnevezése, áthelyezése másik szobába,
// törlése, zárolása és csak olvashatóvá tétele. CSAK admin (a szerver is csak adminnak engedi: topicAction lock, moveTopic, deleteTopic).
//
// A műveletek szerver-hívásai és a megerősítő kérdések a main.js-ben vannak; ez a nézet csak rajzol és a kezelőket hívja.
// ============================================================================

import { t } from "./i18n.js";
import { el } from "./dom.js";
import { getRoomDisplayName } from "./roomName.js";
import { szobaFaSorrend } from "./roomTree.js";
import { temaCim } from "./forumData.js";


/** A témák kezelhető szobái: fórum-módú, nem törlés alatti, nem archivált. A szobafa sorrendjében, behúzva. */
export function kezelhetoSzobak(rooms) {
  return szobaFaSorrend((rooms || []).filter((r) => r && r.forum === true && !r.deleting && !r.archived));
}

/**
 * @param {object} model { uiLang, contentLang, rooms (listRooms), topicsRoomId, topics (null: töltődik | tömb), topicEdit, busy }
 * @param {object} handlers onTopicsRoom(roomId), onTopicEditStart(topicId, mode), onTopicEditChange(mezo, ertek), onTopicEditCancel,
 *   onTopicRename(tema), onTopicMove(tema), onTopicDelete(tema), onTopicLock(tema, bool), onTopicClose(tema, bool)
 * @returns {HTMLElement}
 */
export function renderTemakKezelese(model, handlers) {
  const { uiLang, contentLang, rooms, topicsRoomId, topics, topicEdit, busy } = model;
  const szekcio = el("section", { class: "panel-section admin-topics" });
  szekcio.appendChild(el("h3", { text: t(uiLang, "admin.manageTopics") }));
  szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.topicsHint") }));

  // --- Szoba választó
  const valaszto = el("select", { class: "panel-input", "aria-label": t(uiLang, "admin.topicsRoom"), disabled: busy });
  valaszto.appendChild(el("option", { value: "", text: t(uiLang, "admin.topicsPickRoom") }));
  for (const { room, melyseg } of kezelhetoSzobak(rooms)) {
    const o = el("option", { value: room.roomId, text: "  ".repeat(melyseg) + (melyseg > 0 ? "↳ " : "") + getRoomDisplayName(room, contentLang, uiLang) });
    if (room.roomId === topicsRoomId) o.selected = true;
    valaszto.appendChild(o);
  }
  valaszto.addEventListener("change", () => handlers.onTopicsRoom(valaszto.value || null));
  szekcio.appendChild(valaszto);
  if (!topicsRoomId) return szekcio;

  if (!Array.isArray(topics)) {
    szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
    return szekcio;
  }
  // Az előzmény-téma (az átalakított szoba korábbi chatje) is megjelenik, de csak törölhető (v149).
  const kezelheto = topics;
  if (kezelheto.length === 0) {
    szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.topicsNone") }));
    return szekcio;
  }

  const lista = el("div", { class: "admin-topic-list" });
  for (const tema of kezelheto) {
    const sor = el("div", { class: "admin-topic-row" + (tema.locked ? " locked" : "") });
    const fej = el("div", { class: "admin-topic-head" });
    const elozmeny = tema.kind === "history";
    fej.appendChild(el("span", { class: "admin-topic-title", text: elozmeny ? "📜 " + t(contentLang, "forum.historyTitle") : temaCim(tema, contentLang) }));
    if (elozmeny && Number.isInteger(tema.historyMessageCount)) fej.appendChild(el("span", { class: "admin-topic-badge", text: String(tema.historyMessageCount) }));
    if (tema.locked) fej.appendChild(el("span", { class: "admin-topic-badge", text: "⛔ " + t(uiLang, "admin.topicLockedBadge") }));
    else if (tema.status === "closed" && tema.kind !== "moved") fej.appendChild(el("span", { class: "admin-topic-badge", text: "📢 " + t(uiLang, "admin.topicReadOnlyBadge") }));
    if (tema.kind === "moved") {
      const cel = (rooms || []).find((r) => r.roomId === tema.movedToRoomId);
      fej.appendChild(el("span", { class: "admin-topic-badge", text: "➡️ " + t(uiLang, "admin.topicMovedBadge", { room: cel ? getRoomDisplayName(cel, contentLang, uiLang) : "?" }) }));
    }
    sor.appendChild(fej);

    // Az átirányító téma (30 napig a régi helyen) nem kezelhető: csak törölhető.
    const szerkesztes = topicEdit && topicEdit.topicId === tema.topicId ? topicEdit : null;
    if (szerkesztes && szerkesztes.mode === "rename") {
      const mezo = el("input", { type: "text", class: "panel-input", maxlength: "150", "aria-label": t(uiLang, "admin.topicRename") });
      mezo.value = szerkesztes.value || "";
      mezo.addEventListener("input", () => handlers.onTopicEditChange("value", mezo.value));
      sor.appendChild(mezo);
      sor.appendChild(el("div", { class: "panel-row" }, [
        el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: busy, onclick: handlers.onTopicEditCancel }),
        el("button", { type: "button", class: "primary-btn compact", text: t(uiLang, "admin.topicSave"), disabled: busy, onclick: () => handlers.onTopicRename(tema) }),
      ]));
    } else if (szerkesztes && szerkesztes.mode === "move") {
      sor.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "admin.topicMoveTo") }));
      const cel = el("select", { class: "panel-input", "aria-label": t(uiLang, "admin.topicMoveTo") });
      cel.appendChild(el("option", { value: "", text: t(uiLang, "admin.topicsPickRoom") }));
      for (const { room, melyseg } of kezelhetoSzobak(rooms)) {
        if (room.roomId === topicsRoomId || room.guide === true) continue;
        const o = el("option", { value: room.roomId, text: "  ".repeat(melyseg) + (melyseg > 0 ? "↳ " : "") + getRoomDisplayName(room, contentLang, uiLang) });
        if (room.roomId === szerkesztes.target) o.selected = true;
        cel.appendChild(o);
      }
      cel.addEventListener("change", () => handlers.onTopicEditChange("target", cel.value));
      sor.appendChild(cel);
      sor.appendChild(el("div", { class: "panel-row" }, [
        el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: busy, onclick: handlers.onTopicEditCancel }),
        el("button", { type: "button", class: "primary-btn compact", text: t(uiLang, "admin.topicMoveDo"), disabled: busy, onclick: () => handlers.onTopicMove(tema) }),
      ]));
    } else {
      const gombok = el("div", { class: "admin-topic-actions" });
      const gomb = (kulcs, kezelo, osztaly = "neutral-btn") => gombok.appendChild(el("button", { type: "button", class: osztaly, text: t(uiLang, kulcs), disabled: busy, onclick: kezelo }));
      if (tema.kind === "discussion") {
        if (!tema.locked) {
          gomb("admin.topicRename", () => handlers.onTopicEditStart(tema, "rename"));
          gomb("admin.topicMove", () => handlers.onTopicEditStart(tema, "move"));
          gomb(tema.status === "closed" ? "admin.topicSetWritable" : "admin.topicSetReadOnly", () => handlers.onTopicClose(tema, tema.status !== "closed"));
        }
        gomb(tema.locked ? "admin.topicUnlock" : "admin.topicLock", () => handlers.onTopicLock(tema, !tema.locked));
      }
      gomb("admin.topicDelete", () => handlers.onTopicDelete(tema), "danger-btn");
      sor.appendChild(gombok);
    }
    lista.appendChild(sor);
  }
  szekcio.appendChild(lista);
  // Lapozás (2026-10-19): ha a lap tele volt, lehet még téma - a gomb a következő lapot tölti be.
  if (model.topicsMore === true && handlers.onTopicsMore) {
    szekcio.appendChild(el("button", { type: "button", class: "neutral-btn admin-topics-more", text: t(uiLang, "admin.topicsMore"), disabled: busy, onclick: handlers.onTopicsMore }));
  }
  return szekcio;
}
